import type { UILoadingTip } from '@sorrem-client/types';
import {
    DODGE_COOLDOWN_MS,
    DRAW_DAMAGE_TAKEN,
    ESSENCE_EXCHANGE_RATE,
    INTERCEDE_DURATION_MS,
    SKILL_DECAY_FLOOR_LEVELS,
    SOLACE_RANGE_TILES,
    VEIL_SATURATION_BANDS,
    WEAVE_COST_FRACTION,
} from '@sorrem/shared';

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

/** A small whole number as a tip reads it (`two`), or its digits past ten. */
function numberWord(value: number): string {
    return NUMBER_WORDS[value] ?? String(value);
}

function percent(fraction: number): string {
    return `${Math.round(fraction * 100)}%`;
}

const FAINTEST_BAND = VEIL_SATURATION_BANDS[0]!;
const STRONGEST_BAND = VEIL_SATURATION_BANDS[VEIL_SATURATION_BANDS.length - 1]!;

/**
 * How long a loading screen stays up at the least, so a tip on it can be
 * read. Every tip in the catalog is short enough to read in this time.
 */
export const LOADING_TIP_READ_MS = 7000;

/** The longest a tip may run: a slow reader gets through this many words in `LOADING_TIP_READ_MS`. */
export const LOADING_TIP_MAX_WORDS = 22;

/**
 * What the loading screens teach while the player waits, the counterplay to
 * enemies first. Keys named here are the defaults in @sorrem/shared
 * input-bindings; keep them in step if those change. The numbers come from
 * the shared rules, so a tip never states an old one.
 */
export const LOADING_TIPS: readonly UILoadingTip[] = [
    // Dodging and reading telegraphs.
    { id: 'dodge-early', text: 'Press Space to dodge. Start it just before the blow lands: the first moment of the roll can still be hit.' },
    { id: 'dodge-cooldown', text: `Dodge has about a ${numberWord(Math.round(DODGE_COOLDOWN_MS / 1000))}-second cooldown. Save it for telegraphed attacks instead of using it to travel.` },
    { id: 'dodge-direction', text: 'You roll the way you\'re moving, or the way you face if you\'re standing still.' },
    { id: 'telegraph-step-out', text: 'Floor warnings lock in place when the windup starts. Step out of the marked area and the blow misses.' },
    { id: 'charger-wall', text: 'Keep a wall behind you when a charger lines up. Sidestep, and it stuns itself on the wall.' },
    { id: 'bolts-and-cover', text: 'Enemy bolts stop at walls. Put cover between you and ranged foes, or dodge through the shot.' },
    { id: 'thrown-attacks', text: 'Thrown attacks sail over walls. Get off the landing marker before it hits.' },

    // Interrupts.
    { id: 'break-poise', text: 'Pile damage onto an enemy that is winding up. Breaking its poise staggers it and cancels the attack.' },
    { id: 'poise-bar', text: 'Elites and guardians show a poise bar. Empty it to leave them helpless for a moment.' },
    { id: 'kill-the-windup', text: 'Killing an enemy mid-windup cancels its attack. Finish off whoever is winding up first.' },
    { id: 'stagger-the-mend', text: 'Support enemies mend wounded allies. Stagger one while it mends to cancel the heal.' },
    { id: 'flank-brutes', text: 'Brutes shrug off blows from the front. Flank them, or hit them with Essence abilities, which pass the guard.' },
    { id: 'attack-turns', text: `Only so many enemies attack you at once: ${numberWord(FAINTEST_BAND.concurrentAttackersPerPlayer)} in ${FAINTEST_BAND.displayName}, up to ${numberWord(STRONGEST_BAND.concurrentAttackersPerPlayer)} in ${STRONGEST_BAND.displayName}. Reposition instead of trading blows.` },
    { id: 'force-move', text: 'Hold A to walk toward the cursor without attacking. Use it to break out when you\'re surrounded.' },

    // Essence and abilities.
    { id: 'essence-is-life', text: 'Essence is both your life and your ability fuel. Every cast spends from the pool that keeps you standing.' },
    { id: 'no-regen', text: 'Essence never comes back on its own. Draw, on D unless you move it, is how you recover in a Crucible.' },
    { id: 'ability-picker', text: 'Right-click any action slot to choose which Essence ability it casts, or to clear the slot.' },
    { id: 'cast-and-aim', text: 'Press an action slot\'s key to use it. Point the cursor at your target as you press.' },
    { id: 'rebind-keys', text: 'Click an action slot\'s key plate, then press any free key to move the slot there.' },
    { id: 'solace-ally-only', text: `Solace heals another party member, never you. Point at an ally within ${SOLACE_RANGE_TILES} tiles when you cast it.` },
    { id: 'intercede-cost', text: `Intercede moves an ally's damage onto your pool for ${INTERCEDE_DURATION_MS / 1000} seconds. Watch your own Essence: it can fell you.` },
    { id: 'draw-refill', text: 'Draw is free and you can move while it lasts. Run over your essence orbs: they refill you instead of being carried.' },
    { id: 'draw-rates', text: `Drawn orbs heal by their essence: Truth restores ${numberWord(ESSENCE_EXCHANGE_RATE.truth / ESSENCE_EXCHANGE_RATE.resonance)} times what Resonance does. Draw takes only what you're missing.` },
    { id: 'draw-cancel', text: `Cast Draw again to stop it early. You take ${percent(DRAW_DAMAGE_TAKEN)} more damage while drawing.` },
    { id: 'weave-pair', text: `Cast Weave, then two abilities from different essence types. They go off together for ${percent(WEAVE_COST_FRACTION)} of their cost.` },
    { id: 'weave-traits', text: 'In a Weave, each essence type lends a trait: Fury spreads, Courage steels, Sorrow mends, Memory lingers, Truth pierces, Resonance returns.' },

    // Crystals.
    { id: 'coalesce-crystal', text: 'Press I to open your Carried panel. Click an empty slot and pick a crystal to coalesce it from essence.' },
    { id: 'use-crystal', text: 'Click a carried slot\'s key plate, then press an action slot\'s key to bind it. Hold the key to use a crystal.' },
    { id: 'healing-crystals', text: 'Resonance crystals are your basic heal. Sorrow Vitality crystals restore more and boost the healing you receive.' },
    { id: 'starting-crystals', text: 'Coalesce starting crystals on the gathering screen from banked essence. They\'re ready the moment you enter.' },
    { id: 'crystals-run-bound', text: 'Crystals last only for the run. Any you don\'t use are lost when it ends.' },

    // Progression.
    { id: 'veil-attunement', text: 'Essence you spend on abilities raises Veil Attunement. Its milestones grant attribute points and skill points, kept win or lose.' },
    { id: 'martial-skills-decay', text: `Martial skills grow with each hit you land and fade if unused, but never below levels ${SKILL_DECAY_FLOOR_LEVELS.join(' or ')} once reached.` },
    { id: 'trade-gear', text: 'Trade unwanted gear from the Inventory tab. A house of exchange pours its essence value into your vessel.' },

    // The run and the party.
    { id: 'commission-panel', text: 'Press O during a run to check your commission\'s goals and progress.' },
    { id: 'match-readiness', text: 'When creating a Crucible, pick a Veil Saturation that matches your readiness. The panel shows the recommended range.' },
    { id: 'invite-link', text: 'To bring friends into your Crucible, give them the invite code on the gathering screen, or send them its link.' },
    { id: 'ready-up', text: 'Click Ready? beside your name to ready up. Your gear and crystals lock while you\'re ready.' },
    { id: 'report-bug', text: 'Found a bug? Press F8, or choose Report a bug in settings, to send it straight to the team.' },
];
